'use client';

import { useCallback, useEffect, useRef } from 'react';
import type { CallToActionType, PromotionLocation } from '@fanduel/rewards-hub-client/types/promotion';
import { useStore } from '@/store/store';
import {
    trackRewardsHubPromoClickToAmplitude,
    type RewardsHubPromoClickContext
} from '@/tracking/amplitude/events/rewards-hub/promo-click';
import {
    trackPromoOfferCtaClickedToAmplitude,
    trackPromoOfferTermsExpandedToAmplitude
} from '@/tracking/amplitude/events/rewards-hub/promo-offer';
import { trackPromoCardViewedToAmplitude } from '@/tracking/amplitude/events/rewards-hub/rewards-hub';
import type { RewardsHubSharedState } from '@/tracking/amplitude/events/rewards-hub/rewards-hub';
import {
    REWARDS_LINK_TYPE,
    REWARDS_DEFAULT_LINK_TEXT,
    REWARDS_SOURCE
} from '@/tracking/amplitude/events/rewards-hub/event-names';
import { PageType } from '@/hooks/use-rewards/constants';
import { store, STORAGE_KEYS } from '@/utils/storage';

export type PromotionClickContext = {
    promoCode?: string;
    name?: string;
    position?: number;
    location?: PromotionLocation;
    customerPromotionState?: { optInState?: string };
    callToAction?: { action?: CallToActionType };
};

type UseRewardsPromoTrackingOptions = {
    sharedStateRef?: React.RefObject<RewardsHubSharedState | null>;
    pageType?: PageType.HUB | PageType.OFFER;
};

export const mapPromotionToClickContext = (promotion: PromotionClickContext): RewardsHubPromoClickContext => ({
    promoId: promotion?.promoCode,
    promoName: promotion?.name,
    position: promotion?.position,
    location: promotion?.location ?? undefined
});

const mapLocationToNavigationSource = (location: unknown): string =>
    location === 'carousel' ? REWARDS_SOURCE.CAROUSEL_MORE_INFO : REWARDS_SOURCE.PROMO_HUB;

export const storePromoNavigationSource = (location: unknown): void => {
    store({ key: STORAGE_KEYS.NAVIGATION_SOURCE, value: mapLocationToNavigationSource(location) }, 'session');
};

export const useRewardsPromoTracking = (options?: UseRewardsPromoTrackingOptions) => {
    const sendEvent = useStore((s) => s.tracking.sendEvent);
    const sendEventRef = useRef(sendEvent);

    useEffect(() => {
        sendEventRef.current = sendEvent;
    }, [sendEvent]);

    const pageType = options?.pageType || PageType.HUB;

    const onPrimaryLinkClicked = useCallback(
        (promotion: PromotionClickContext, innerText: string | undefined) => {
            const linkText = innerText ?? REWARDS_DEFAULT_LINK_TEXT.PRIMARY_CTA;

            if (pageType === PageType.OFFER) {
                trackPromoOfferCtaClickedToAmplitude({
                    sendEvent: sendEventRef.current,
                    sharedState: options?.sharedStateRef?.current,
                    linkText,
                    context: {
                        promoId: promotion?.promoCode,
                        promoName: promotion?.name,
                        optedIn: promotion?.customerPromotionState?.optInState
                    }
                });
            } else {
                trackRewardsHubPromoClickToAmplitude({
                    sendEvent: sendEventRef.current,
                    sharedState: options?.sharedStateRef?.current,
                    type: REWARDS_LINK_TYPE.PRIMARY,
                    linkText,
                    context: mapPromotionToClickContext(promotion)
                });
            }
        },
        [options?.sharedStateRef, pageType]
    );

    const onSecondaryLinkClicked = useCallback(
        (promotion: PromotionClickContext, innerText: string | undefined) => {
            const linkText = innerText ?? REWARDS_DEFAULT_LINK_TEXT.SECONDARY_CTA;

            if (pageType === PageType.OFFER) {
                trackPromoOfferCtaClickedToAmplitude({
                    sendEvent: sendEventRef.current,
                    sharedState: options?.sharedStateRef?.current,
                    linkText,
                    context: {
                        promoId: promotion?.promoCode,
                        promoName: promotion?.name,
                        optedIn: promotion?.customerPromotionState?.optInState
                    }
                });
            } else {
                storePromoNavigationSource(promotion?.location);
                trackRewardsHubPromoClickToAmplitude({
                    sendEvent: sendEventRef.current,
                    sharedState: options?.sharedStateRef?.current,
                    type: REWARDS_LINK_TYPE.SECONDARY,
                    linkText,
                    context: mapPromotionToClickContext(promotion)
                });
            }
        },
        [options?.sharedStateRef, pageType]
    );

    const onCardClicked = useCallback(
        (promotion: PromotionClickContext, imageUrl: string) => {
            storePromoNavigationSource(promotion?.location);
            trackRewardsHubPromoClickToAmplitude({
                sendEvent: sendEventRef.current,
                sharedState: options?.sharedStateRef?.current,
                type: REWARDS_LINK_TYPE.IMAGE,
                linkText: imageUrl,
                context: mapPromotionToClickContext(promotion)
            });
        },
        [options?.sharedStateRef]
    );

    const onTermsExpanded = useCallback(
        ({ promoId, promoName }: { promoId: string; promoName: string | undefined }) => {
            trackPromoOfferTermsExpandedToAmplitude({
                sendEvent: sendEventRef.current,
                sharedState: options?.sharedStateRef?.current,
                context: { promoId, promoName }
            });
        },
        [options?.sharedStateRef]
    );

    const onVisibleCardCallback = useCallback(
        (promotion: PromotionClickContext) => {
            trackPromoCardViewedToAmplitude({
                sendEvent: sendEventRef.current,
                sharedState: options?.sharedStateRef?.current,
                context: mapPromotionToClickContext(promotion)
            });
        },
        [options?.sharedStateRef]
    );

    return {
        onPrimaryLinkClicked,
        onSecondaryLinkClicked,
        onCardClicked,
        onTermsExpanded,
        onVisibleCardCallback,
        sendEventRef
    };
};
